import { BookableSlot, fromAPI } from "@cur8/rich-entity";
import { silenceAbort } from "lib/error";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { useAPIClient } from "render/context/APIContext";
import { querySlots } from "render/views/booking/MultiSiteMonthView/api/slot";
import { BookableTimeSlot } from "./components/BookableTimeSlot";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

interface ListViewProps {
  date: DateTime;
  siteIds: string[];
  selectedSlotId: string | undefined;
  onSelect: (slot: BookableSlot) => void;
  onSiteResetClick: () => void;
  allSitesSelected: boolean;
}

export function ListView({
  date,
  siteIds,
  onSelect,
  onSiteResetClick,
  selectedSlotId,
  allSitesSelected,
}: ListViewProps) {
  const [slots, setSlots] = useState<BookableSlot[]>();
  const api = useAPIClient();
  const dateISO = date.toISODate();
  const siteIdsFormatted = siteIds.join(",");

  useEffect(() => {
    const targetDate = DateTime.fromISO(dateISO);
    const targetSiteIds = siteIdsFormatted.split(",");

    const start = DateTime.max(DateTime.now(), targetDate.startOf("day"));
    const end = targetDate.endOf("day");

    const req = querySlots(api, {
      siteIds: targetSiteIds,
      pageSize: 100,
      start,
      end,
    });

    req.result
      .then((result) => setSlots(result.items.map(fromAPI.toBookableSlot)))
      .catch(silenceAbort);
    return () => {
      req.abandon();
    };
  }, [api, dateISO, siteIdsFormatted]);

  const select = useCallback(
    (slot: BookableSlot) => () => {
      return onSelect(slot);
    },
    [onSelect]
  );
  const isSelected = useCallback(
    (slot: BookableSlot) => {
      return slot.slotId === selectedSlotId;
    },
    [selectedSlotId]
  );

  if (!slots) {
    return null;
  }

  return (
    <div className={styles.ListView}>
      {slots.length === 0 && (
        <div className={styles.empty}>
          {allSitesSelected && (
            <>
              <div className={styles.head}>
                <Trans.AllSites.Header />
              </div>
              <div className={styles.body}>
                <Trans.AllSites.Description />
              </div>
            </>
          )}
          {!allSitesSelected && (
            <>
              <div className={styles.head}>
                <Trans.Site.Header />
              </div>
              <div className={styles.body}>
                <Trans.Site.Description />
              </div>
              <button onClick={onSiteResetClick} className={styles.link}>
                <Trans.Site.ResetSites />
              </button>
            </>
          )}
        </div>
      )}
      {slots.length > 0 && (
        <ul>
          {slots.map((slot) => {
            return (
              <li key={slot.slotId}>
                <BookableTimeSlot
                  isSelected={isSelected(slot)}
                  onClick={select(slot)}
                  slot={slot}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
